/**
 * 数字人「逐句对照编辑区」(2026-10-10 追爆出口原话复用 §3.1)。
 *
 * 每句:角色标签 + 原话/生成稿 + 三档下拉(原话 / 套句式 / 按我的产品重写);顶部三预设批量设档位,
 * 默认 A「原样翻拍」。含参考片事实的原话句 / 改写句里出现产品事实外的数字 → 黄标 +「我确认可以用」。
 *
 * 纯展示 + 回调:状态全在页面(`DigitalHumanCreatePage`),逻辑在 `model/dhBlueprintLines`。
 * 「最终口播」大框仍在页面 Stage 里,是唯一上送源 —— 这里改动由页面实时拼进去。
 */
import { AlertTriangle, Loader2, Package, Plus, Sparkles, X } from "lucide-react";

import { LINE_ROLE_LABEL, REF_FACT_KIND_LABEL } from "@/entities/explosive/model/blueprintLines";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";

import {
  DH_LINE_MODE_LABEL,
  DH_LINE_PRESETS,
  displayFlagOf,
  isPending,
  rowText,
  type DhLineMode,
  type DhLinePreset,
  type DhLineRow,
} from "../../model/dhBlueprintLines";

const MODES: DhLineMode[] = ["keep", "swap", "rewrite"];

export function DhBlueprintLinesEditor({
  rows,
  preset,
  productLabel,
  generating,
  disabled,
  hints,
  manualEdited,
  showAddProductLine,
  onPreset,
  onRowMode,
  onConfirm,
  onPickProduct,
  onGenerate,
  onAddProductLine,
  onRemoveAdded,
  onResync,
}: {
  rows: DhLineRow[];
  /** 当前高亮的预设;逐句单独改过档位 → null(混合)。 */
  preset: DhLinePreset | null;
  productLabel: string | null;
  generating: boolean;
  disabled?: boolean;
  hints: string[];
  manualEdited: boolean;
  showAddProductLine: boolean;
  onPreset: (p: DhLinePreset) => void;
  onRowMode: (idx: number, mode: DhLineMode) => void;
  onConfirm: (idx: number, confirmed: boolean) => void;
  onPickProduct: () => void;
  onGenerate: () => void;
  onAddProductLine: () => void;
  onRemoveAdded: (idx: number) => void;
  onResync: () => void;
}) {
  const pendingCount = rows.filter(isPending).length;
  const locked = disabled || generating;

  return (
    <div
      data-testid="dh-lines-editor"
      className="space-y-3 rounded-lg border border-border bg-card/60 px-3 py-3 sm:px-4"
    >
      {/* 用法:三预设(只是批量设档位) */}
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[13.5px] font-semibold text-foreground">逐句对照</span>
          <span className="text-2xs text-muted-foreground">
            每句可选:原话 / 套句式(结构不动,只换产品·卖点·数字)/ 按我的产品重写
          </span>
        </div>
        <div role="radiogroup" aria-label="用法" className="flex flex-wrap gap-1.5">
          {DH_LINE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={preset === p.key}
              data-testid={`dh-lines-preset-${p.key}`}
              title={p.hint}
              disabled={locked}
              onClick={() => onPreset(p.key)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-2xs transition-colors disabled:opacity-60",
                preset === p.key
                  ? "border-primary bg-primary/10 font-semibold text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              )}
            >
              {p.label}
              {p.key === "verbatim" && <span className="ml-1 opacity-70">(默认)</span>}
            </button>
          ))}
        </div>
      </div>

      {/* 我的产品 */}
      <div className="flex flex-wrap items-center gap-2 text-2xs">
        <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground">我的产品:</span>
        <span
          data-testid="dh-lines-product"
          className={cn("min-w-0 truncate font-medium", !productLabel && "text-muted-foreground")}
        >
          {productLabel ?? "未选"}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 px-2 text-2xs"
          data-testid="dh-lines-pick-product"
          disabled={locked}
          onClick={onPickProduct}
        >
          {productLabel ? "换产品" : "选产品"}
        </Button>
        <span className="basis-full text-muted-foreground sm:basis-auto">
          选了产品,参考片里的品牌/产品名会自动换成你的
        </span>
      </div>

      {/* 逐句 */}
      <ol className="divide-y divide-border/60 rounded-md border border-border/60 bg-background/60">
        {rows.map((r, i) => {
          const flag = displayFlagOf(r, !!productLabel);
          const flagged =
            flag.confirm.length > 0 ||
            flag.unverified.length > 0 ||
            flag.unverifiedFacts.length > 0;
          const pending = isPending(r);
          return (
            <li
              key={`${r.idx}-${r.added ? "a" : "o"}`}
              data-testid={`dh-line-${i}`}
              className="space-y-1.5 px-2.5 py-2"
            >
              {/* 窄屏:下拉框换到句子下一行(1010 打磨:390 宽时下拉框挤得一句折四五行) */}
              <div className="flex flex-wrap items-start gap-x-2 gap-y-1 sm:flex-nowrap">
                <span className="mt-0.5 shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {r.added ? "新增" : LINE_ROLE_LABEL[r.role]}
                </span>
                <p
                  data-testid={`dh-line-text-${i}`}
                  className={cn(
                    "min-w-0 flex-1 basis-[calc(100%-3.5rem)] text-xs leading-relaxed sm:basis-auto",
                    pending && "text-muted-foreground line-through decoration-muted-foreground/40"
                  )}
                >
                  {rowText(r)}
                </p>
                {r.added ? (
                  <button
                    type="button"
                    aria-label="去掉这句产品介绍"
                    disabled={locked}
                    onClick={() => onRemoveAdded(r.idx)}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <select
                    aria-label={`第 ${i + 1} 句怎么用`}
                    data-testid={`dh-line-mode-${i}`}
                    value={r.mode}
                    disabled={locked}
                    onChange={(e) => onRowMode(r.idx, e.target.value as DhLineMode)}
                    className="ml-auto shrink-0 rounded-md border border-border/60 bg-background px-1.5 py-1 text-2xs sm:ml-0"
                  >
                    {MODES.map((m) => (
                      <option key={m} value={m}>
                        {DH_LINE_MODE_LABEL[m]}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {pending && (
                <p className="pl-1 text-[11px] text-muted-foreground">
                  待生成 —— 点下方「生成改写」后换成{DH_LINE_MODE_LABEL[r.mode]}的版本
                </p>
              )}
              {flagged && (
                <div
                  data-testid={`dh-line-flag-${i}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-300/70 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                >
                  <span className="inline-flex min-w-0 items-start gap-1">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                      {flag.confirm.length > 0 &&
                        `含参考片的${[...new Set(flag.confirm.map((f) => REF_FACT_KIND_LABEL[f.kind]))].join("/")}(${flag.confirm.map((f) => f.span).join("/")})`}
                      {flag.confirm.length > 0 && flag.unverified.length > 0 && ";"}
                      {flag.unverified.length > 0 &&
                        `数字 ${flag.unverified.join("/")} 不在你的产品事实里`}
                      {(flag.confirm.length > 0 || flag.unverified.length > 0) &&
                        flag.unverifiedFacts.length > 0 &&
                        ";"}
                      {flag.unverifiedFacts.length > 0 &&
                        `「${flag.unverifiedFacts.join("/")}」不在你的产品事实里(像是照搬参考片)`}
                    </span>
                  </span>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      data-testid={`dh-line-confirm-${i}`}
                      checked={r.confirmed}
                      disabled={locked}
                      onChange={(e) => onConfirm(r.idx, e.target.checked)}
                    />
                    我确认可以用
                  </label>
                  {r.mode === "keep" && !r.added && (
                    <button
                      type="button"
                      disabled={locked}
                      className="underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-100"
                      onClick={() => onRowMode(r.idx, "swap")}
                    >
                      改成我的
                    </button>
                  )}
                </div>
              )}
              {flag.replaceHint.length > 0 && (
                <p
                  data-testid={`dh-line-replace-hint-${i}`}
                  className="pl-1 text-[11px] text-muted-foreground"
                >
                  含参考片的产品名({flag.replaceHint.map((f) => f.span).join("/")})——
                  选产品后会自动换成你的
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {showAddProductLine && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border px-2.5 py-2 text-2xs text-muted-foreground">
          <span>参考片口播没讲产品 ——</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 gap-1 px-2 text-2xs"
            data-testid="dh-lines-add-product"
            disabled={locked}
            onClick={onAddProductLine}
          >
            <Plus className="h-3 w-3" />
            加一句我的产品介绍
          </Button>
        </div>
      )}

      {pendingCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            data-testid="dh-lines-generate"
            disabled={locked}
            onClick={onGenerate}
          >
            {generating ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-1.5 h-4 w-4" />
            )}
            生成改写({pendingCount} 句)
          </Button>
          <span className="text-2xs text-muted-foreground">
            只改你选了套句式/重写的句子,原话句一字不动
          </span>
        </div>
      )}
      {generating && pendingCount === 0 && (
        <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          正在按你的产品事实改写…
        </p>
      )}

      {hints.length > 0 && (
        <ul
          data-testid="dh-lines-hints"
          className="space-y-0.5 text-2xs text-amber-700 dark:text-amber-400"
        >
          {hints.map((h) => (
            <li key={h}>· {h}</li>
          ))}
        </ul>
      )}

      {manualEdited && (
        <div
          data-testid="dh-lines-manual"
          className="flex flex-wrap items-center gap-2 rounded-md bg-muted px-2.5 py-1.5 text-2xs text-muted-foreground"
        >
          <span>最终口播已手改 —— 逐句区的改动不再回写覆盖你的稿子。</span>
          <button
            type="button"
            disabled={locked}
            className="font-medium text-primary underline underline-offset-2"
            onClick={onResync}
          >
            用逐句区重新拼一份
          </button>
        </div>
      )}
    </div>
  );
}
